import { useRef } from 'react';
import { Textarea } from '@chakra-ui/react';
import { useWatch, type Control, type UseFormRegister } from 'react-hook-form';
import type { ContactFormData } from '@/components/services/contact/ContactForm';
import { useAutoResizeTextarea } from '@/hooks/ui/useAutoResizeTextarea';
import { getInputStyles } from '@/components/ui/inputStyles';

interface ContactNotesFieldProps {
	control: Control<ContactFormData>;
	register: UseFormRegister<ContactFormData>;
	placeholder: string;
}

/**
 * The field look `Field` would have spread onto a bare Textarea: it clones its direct child, and
 * this component stands in between, so the styles are applied here instead.
 */
const STYLES = getInputStyles({ rows: 2 });
/** Empty, the field is the app's usual multi-line height (three lines); past that it grows, uncapped. */
const MIN_HEIGHT_PX = 96;

/**
 * The one field of the contact form that runs to more than a line, sized to what it holds.
 *
 * Its own component so only this field subscribes to its value: watching `notes` here re-renders
 * this textarea per keystroke rather than the whole form, and the same subscription re-measures
 * after a `reset` or a values sync, which land in the field without an input event.
 */
const ContactNotesField = (props: ContactNotesFieldProps) => {
	const { control, register, placeholder } = props;
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);
	const value = useWatch({ control, name: 'notes' }) ?? '';
	useAutoResizeTextarea(textareaRef, value, { minHeight: MIN_HEIGHT_PX });

	const { ref: registerRef, ...notesField } = register('notes');

	return (
		<Textarea
			{...notesField}
			ref={(element) => {
				registerRef(element);
				textareaRef.current = element;
			}}
			placeholder={placeholder}
			textStyle="body"
			rows={1}
			resize="none"
			{...STYLES}
			py={3}
			minH={`${MIN_HEIGHT_PX}px`}
			overflow="hidden"
		/>
	);
};

export default ContactNotesField;
